/**
 * rng.ts — small, deterministic, seedable RNG (mulberry32 + a string hasher).
 *
 * Used by MockTransport so the standalone game produces reproducible,
 * provably-fair-style outcomes from (serverSeed, clientSeed, nonce).
 */

/** xmur3 string hash -> 32-bit seed generator. */
export function xmur3(str: string): () => number {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    return h >>> 0;
  };
}

/** mulberry32 PRNG. Returns floats in [0,1). */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export class Rng {
  private next: () => number;

  constructor(seedStr: string) {
    const seedFn = xmur3(seedStr);
    this.next = mulberry32(seedFn());
  }

  /** float in [0,1). */
  float(): number {
    return this.next();
  }

  /** float in [min,max). */
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /** integer in [min,max] inclusive. */
  int(min: number, max: number): number {
    return Math.floor(this.range(min, max + 1));
  }

  /** pick one item. */
  pick<T>(arr: readonly T[]): T {
    return arr[Math.floor(this.next() * arr.length)];
  }

  /** weighted choice over parallel arrays of values + weights; returns the value. */
  weighted<T>(values: readonly T[], weights: readonly number[]): T {
    let total = 0;
    for (const w of weights) total += w;
    let r = this.next() * total;
    for (let i = 0; i < values.length; i++) {
      r -= weights[i];
      if (r <= 0) return values[i];
    }
    return values[values.length - 1];
  }

  /** Fisher–Yates shuffle (in place), returns the array. */
  shuffle<T>(arr: T[]): T[] {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(this.next() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }
}

/** Cheap synchronous "hash" string for a fake serverSeedHash. */
export function pseudoHash(input: string): string {
  const h = xmur3(input);
  let out = '';
  for (let i = 0; i < 8; i++) out += (h() >>> 0).toString(16).padStart(8, '0');
  return out.slice(0, 64);
}

/** Random-ish hex string for seeds / ids (not crypto). */
export function randomHex(len = 32): string {
  let out = '';
  const chars = '0123456789abcdef';
  for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * 16)];
  return out;
}
